import { useSearchParams } from 'react-router-dom';
import type { SortDir } from '../types/common';

export interface TableState {
  page: number;
  sortBy: string;
  sortDir: SortDir;
  sortStatus: { columnAccessor: string; direction: SortDir };
  filterBy: string;
  setPage: (page: number) => void;
  setSortStatus: (status: { columnAccessor: string; direction: SortDir }) => void;
  setFilterBy: (value: string) => void;
  getFilter: (name: string) => string;
  setFilter: (name: string, value: string) => void;
}

export function useTableState(
  defaultSortBy = 'createdAt',
  defaultSortDir: SortDir = 'desc',
): TableState {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = parseInt(searchParams.get('page') ?? '1', 10);
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? pageParam : 1;
  const sortBy = searchParams.get('sortBy') ?? defaultSortBy;
  const sortDir: SortDir = searchParams.get('sortDir') === 'asc' ? 'asc' : 'desc';
  const filterBy = searchParams.get('filterBy') ?? '';

  const setPage = (newPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (newPage <= 1) {
      next.delete('page');
    } else {
      next.set('page', String(newPage));
    }
    setSearchParams(next);
  };

  const setSortStatus = (status: { columnAccessor: string; direction: SortDir }) => {
    const next = new URLSearchParams(searchParams);
    if (status.columnAccessor === defaultSortBy) {
      next.delete('sortBy');
    } else {
      next.set('sortBy', status.columnAccessor);
    }
    if (status.direction === defaultSortDir) {
      next.delete('sortDir');
    } else {
      next.set('sortDir', status.direction);
    }
    next.delete('page');
    setSearchParams(next);
  };

  const getFilter = (name: string) => searchParams.get(name) ?? '';

  const setFilter = (name: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(name, value);
    } else {
      next.delete(name);
    }
    next.delete('page');
    setSearchParams(next);
  };

  const setFilterBy = (value: string) => setFilter('filterBy', value);

  return {
    page,
    sortBy,
    sortDir,
    sortStatus: { columnAccessor: sortBy, direction: sortDir },
    filterBy,
    setPage,
    setSortStatus,
    setFilterBy,
    getFilter,
    setFilter,
  };
}
